import React from 'react'
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  useTheme,
  alpha,
  Box,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Dashboard,
  Category,
  Person,
  NotificationImportant,
  CurrencyRupee
} from '@mui/icons-material'

const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const getValueFromPath = (path: string): number => {
    switch (path) {
      case '/dashboard':
        return 0
      case '/transactions':
        return 1
      case '/loans':
        return 2
      case '/reminders':
        return 3
      case '/profile':
        return 4
      default:
        return 0
    }
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const paths = ['/dashboard', '/transactions', '/loans', '/reminders', '/profile']
    navigate(paths[newValue])
  }

  const navigationItems = [
    { label: 'Home', icon: <Dashboard />, badge: 0 },
    { label: 'Money', icon: <CurrencyRupee />, badge: 0 },
    { label: 'EMI', icon: <Category />, badge: 0 },
    { label: 'Reminder', icon: <NotificationImportant />, badge: 0 },
    { label: 'Profile', icon: <Person />, badge: 0 },
  ]

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper,
        backdropFilter: 'blur(20px)',
        boxShadow: `0 -2px 16px ${alpha(theme.palette.common.black, 0.08)}`,
        // Ensure it works with safe areas and doesn't interfere with content
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Prevent content from scrolling behind
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: 0,
          right: 0,
          height: 1,
          background: theme.palette.divider,
          opacity: 0.12,
        },
      }}
      elevation={0}
    >
      <BottomNavigation
        value={getValueFromPath(location.pathname)}
        onChange={handleChange}
        showLabels
        sx={{
          height: 64,
          backgroundColor: 'transparent',
          px: 0,
          py: 0,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '4px 8px',
            borderRadius: 2,
            margin: '0 2px',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                fontWeight: 600,
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.3rem',
              },
            },
            '&:not(.Mui-selected)': {
              color: theme.palette.text.secondary,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            fontWeight: 500,
            marginTop: 2,
            transition: 'all 0.2s ease',
            lineHeight: 1.2,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.2rem',
            transition: 'all 0.2s ease',
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.label}
            label={item.label}
            icon={
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.badge > 0 ? (
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    variant="dot"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: -3,
                        minWidth: 6,
                        height: 6,
                        borderRadius: '50%',
                        border: `1px solid ${theme.palette.background.paper}`,
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </Box>
            }
          />
        ))}
      </BottomNavigation>
    </Paper>
  )
}

export default MobileBottomNav